import React, { useState } from 'react';
import {
    Modal,
    Paper,
    Typography,
    Box,
    IconButton,
    Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ActionControls from '../whook/ActionControls';
import MetaData from './MetaData';
import LogTrail from './LogTrail';
import CorrectionEditorModal from './CorrectionEditorModal';
import AutonomyPromotionModal from './AutonomyPromotionModal';
import NotifyReviewerModal from './NotifyReviewerModal';
import DenialReasonModal from './DenialReasonModal';
import AgentLogPanel from './AgentLogPanel';

const WhookEventModal = ({ event, onClose }) => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [editorOpen, setEditorOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [autonomyOpen, setAutonomyOpen] = useState(false);
    const [notifyOpen, setNotifyOpen] = useState(false);
    const [denialOpen, setDenialOpen] = useState(false);
    const [denialReason, setDenialReason] = useState('');
    const [logPanelOpen, setLogPanelOpen] = useState(false);

    if (!event) return null;

    const handleActionTrigger = (actionType) => {
        if (actionType === 'deny') setDenialOpen(true);
        else {
            setPendingAction(actionType);
            setEditorOpen(true);
        }
    };

    const handleEditorClose = () => {
        setEditorOpen(false);
        setPendingAction(null);
    };

    // ✅ Reintroduce your mock data for the autonomy modal
    const approvalHistory = [
        { user: 'Alex Grant', date: 'May 1, 2025', action: 'approve', hadEdits: false, votedForAutonomy: true, avatar: 'https://i.pravatar.cc/40?u=alex' },
        { user: 'Maya Thomas', date: 'May 2, 2025', action: 'approve', hadEdits: true, editsSummary: 'Changed venue', avatar: 'https://i.pravatar.cc/40?u=maya' },
        { user: 'Jordan Lee', date: 'May 3, 2025', action: 'deny', denialReason: 'Incorrect session linked.', avatar: 'https://i.pravatar.cc/40?u=jordan' }
    ];

    const eligibleReviewers = [
        { id: 'u123', name: 'Jordan Lee', avatar: 'https://i.pravatar.cc/40?u=jordan' },
        { id: 'u456', name: 'Maya Thomas', avatar: 'https://i.pravatar.cc/40?u=maya' },
        { id: 'u789', name: 'Alex Grant', avatar: 'https://i.pravatar.cc/40?u=alex' }
    ];

    const reviewHistory = [
        { name: 'Jordan Lee', date: 'May 3, 2025', status: 'Approved', avatar: 'https://i.pravatar.cc/40?u=jordan' },
        { name: 'Maya Thomas', date: 'May 4, 2025', status: 'Reviewing', avatar: 'https://i.pravatar.cc/40?u=maya' }
    ];

    return (
        <>
            <Modal open={!!event} onClose={onClose}>
                <Paper
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        p: 4,
                        width: isFullscreen ? '90vw' : 420,
                        height: isFullscreen ? '90vh' : 'auto',
                        maxWidth: '95vw',
                        maxHeight: '95vh',
                        overflowY: 'auto',
                        bgcolor: '#202020',
                        color: '#fff',
                        border: '1px solid #333',
                        borderRadius: 2
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6" sx={{ color: '#12c099' }}>
                            {event.agent}
                        </Typography>
                        <Box>
                            <Button
                                variant="text"
                                size="small"
                                sx={{ color: '#12c099', mr: 1 }}
                                onClick={() => setIsFullscreen(!isFullscreen)}
                            >
                                {isFullscreen ? 'Shrink' : 'Expand'}
                            </Button>
                            <IconButton onClick={onClose} sx={{ color: '#fff' }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>

                    <Typography variant="body1" sx={{ mb: 2 }}>{event.message}</Typography>
                    <Typography variant="subtitle2" sx={{ color: '#ccc' }}>Status: {event.status}</Typography>
                    <Typography variant="subtitle2" sx={{ color: '#ccc' }}>Timestamp: {event.time}</Typography>

                    {/* ✅ Clickable internal role */}
                    {event.meta?.internalRole && (
                        <Typography
                            variant="subtitle2"
                            sx={{
                                color: '#12c099',
                                mt: 2,
                                cursor: 'pointer',
                                textDecoration: 'underline'
                            }}
                            onClick={() => setLogPanelOpen(true)}
                        >
                            Internal Role: {event.meta.internalRole}
                        </Typography>
                    )}

                    {event.meta && <MetaData meta={event.meta} />}
                    {event.logs?.length > 0 && <LogTrail logs={event.logs} />}

                    <Box sx={{ mt: 4 }}>
                        <ActionControls event={event} onTrigger={handleActionTrigger} />
                    </Box>

                    <Box sx={{ mt: 4, textAlign: 'center' }}>
                        <Button
                            variant="text"
                            sx={{ color: '#12c099' }}
                            onClick={() => setAutonomyOpen(true)}
                        >
                            View Autonomy Approval History
                        </Button>
                    </Box>
                </Paper>
            </Modal>

            {/* ✅ Correction Modal */}
            <CorrectionEditorModal open={editorOpen} onClose={handleEditorClose} event={event} actionType={pendingAction} />

            {/* ✅ Restored full Autonomy Modal */}
            <AutonomyPromotionModal
                open={autonomyOpen}
                onClose={() => setAutonomyOpen(false)}
                onConfirm={() => {
                    console.log('✔ Promoted to autonomous mode');
                    setAutonomyOpen(false);
                }}
                approvalHistory={approvalHistory}
                requiredApprovals={3}
                onNotify={() => setNotifyOpen(true)}
            />

            {/* ✅ Notify Reviewer Modal */}
            <NotifyReviewerModal
                open={notifyOpen}
                onClose={() => setNotifyOpen(false)}
                eligibleReviewers={eligibleReviewers}
                reviewHistory={reviewHistory}
                onNotify={(userId, note) => {
                    const user = eligibleReviewers.find(u => u.id === userId);
                    console.log(`📨 Sent notification to ${user.name} with note: "${note}"`);
                    setNotifyOpen(false);
                }}
            />

            {/* ✅ Denial Reason Modal */}
            <DenialReasonModal
                open={denialOpen}
                onClose={() => {
                    setDenialOpen(false);
                    setDenialReason('');
                }}
                onSubmit={(reason) => {
                    console.log('❌ Denial reason submitted:', reason);
                    setDenialOpen(false);
                    setDenialReason('');
                }}
                event={event}
            />

            {/* ✅ Linked Agent Log Panel */}
            <AgentLogPanel open={logPanelOpen} onClose={() => setLogPanelOpen(false)} agent={event.agent} />
        </>
    );
};

export default WhookEventModal;
